/**
 * HC - Collections Carousel JavaScript
 * Handles carousel functionality with touch/swipe support and responsive behavior
 */

class CollectionsCarousel {
  constructor(container) {
    this.container = container;
    this.wrapper = container.querySelector('.collections-carousel__wrapper');
    this.track = container.querySelector('[data-carousel-track]');
    this.cards = container.querySelectorAll('.collections-carousel__card');
    this.prevButton = container.querySelector('[data-carousel-prev]');
    this.nextButton = container.querySelector('[data-carousel-next]');
    this.dots = container.querySelectorAll('[data-carousel-dot]');
    
    this.currentIndex = 0;
    this.totalCards = this.cards.length;
    this.isAnimating = false;
    this.autoplayInterval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.handleResize = this.handleResize.bind(this);
    
    this.init();
  }
  
  init() {
    if (this.totalCards <= 1) return;
    
    this.setupEventListeners();
    this.updateNavigation();
    this.startAutoplay();
    this.updateDots();
  }
  
  setupEventListeners() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToPrevious());
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToNext());
    }
    
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Touch/swipe support
    this.setupTouchEvents();
    
    // Keyboard navigation
    this.setupKeyboardEvents();
    
    // Pause autoplay on hover
    this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.container.addEventListener('mouseleave', () => this.startAutoplay());
    
    // Visibility change (pause when tab is not active)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.startAutoplay();
      }
    });

    window.addEventListener('resize', this.handleResize);
  }
  
  setupTouchEvents() {
    this.track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      this.pauseAutoplay();
    }, { passive: true });
    
    this.track.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
      this.startAutoplay();
    }, { passive: true });
  }
  
  setupKeyboardEvents() {
    this.container.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.goToNext();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.totalCards - 1);
          break;
      }
    });
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next slide
        this.goToNext();
      } else {
        // Swipe right - previous slide
        this.goToPrevious();
      }
    }
  }
  
  goToSlide(index) {
    if (this.isAnimating) return;
    
    const maxIndex = this.getMaxIndex();
    const clampedIndex = Math.max(0, Math.min(index, maxIndex));
    
    if (clampedIndex === this.currentIndex) {
      this.setTrackPosition();
      this.updateNavigation();
      this.updateDots();
      return;
    }
    
    this.isAnimating = true;
    this.currentIndex = clampedIndex;
    
    this.setTrackPosition();
    
    // Update navigation state
    this.updateNavigation();
    this.updateDots();
    
    // Reset animation flag after transition
    setTimeout(() => {
      this.isAnimating = false;
    }, 500);
  }
  
  // Helper method to parse CSS gap value (handles "px", "rem", etc.)
  parseGapValue(gapString) {
    if (!gapString) return 16;
    
    // Remove "px" suffix if present and parse
    const numericValue = parseFloat(gapString);
    if (isNaN(numericValue)) return 16;
    
    // If it's in rem, convert to pixels (assuming 16px base)
    if (gapString.includes('rem')) {
      return numericValue * 16;
    }
    
    return numericValue;
  }
  
  // Helper method to get visible cards count
  getVisibleCardsCount() {
    // Use wrapper width for more accurate calculation (accounts for overflow)
    const wrapperElement = this.wrapper || this.container;
    const wrapperRect = wrapperElement.getBoundingClientRect();
    const wrapperWidth = wrapperRect.width;
    
    // Use getBoundingClientRect for more accurate card width
    const cardRect = this.cards[0].getBoundingClientRect();
    const cardWidth = cardRect.width;
    
    // Parse gap value properly (handles "px", "rem", etc.)
    const gapString = getComputedStyle(this.track).gap;
    const gap = this.parseGapValue(gapString);
    
    // Calculate visible cards with tolerance buffer (1-2px) to account for flexbox rounding
    const tolerance = 2;
    const visibleCount = Math.floor((wrapperWidth + tolerance) / (cardWidth + gap));
    
    // Ensure we return at least 1 and at most totalCards
    return Math.max(1, Math.min(visibleCount, this.totalCards));
  }
  
  goToNext() {
    const nextIndex = this.currentIndex + 1;
    const maxIndex = this.getMaxIndex();
    if (nextIndex <= maxIndex) {
      this.goToSlide(nextIndex);
    } else {
      this.goToSlide(maxIndex);
    }
  }
  
  goToPrevious() {
    const prevIndex = this.currentIndex - 1;
    // Allow sliding back to show the previous set of cards
    if (prevIndex >= 0) {
      this.goToSlide(prevIndex);
    }
  }
  
  updateNavigation() {
    // Update button states based on current position
    const visibleCards = Math.max(1, this.getVisibleCardsCount());
    const maxIndex = this.getMaxIndex();
    
    // If all cards are visible, disable both buttons
    const allCardsVisible = this.totalCards <= visibleCards;
    
    if (this.prevButton) {
      const shouldDisablePrev = allCardsVisible || this.currentIndex === 0;
      this.prevButton.disabled = shouldDisablePrev;
      this.prevButton.setAttribute('aria-disabled', shouldDisablePrev);
    }
    
    if (this.nextButton) {
      const shouldDisableNext = allCardsVisible || this.currentIndex >= maxIndex;
      this.nextButton.disabled = shouldDisableNext;
      this.nextButton.setAttribute('aria-disabled', shouldDisableNext);
    }
  }
  
  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('collections-carousel__dot--active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('collections-carousel__dot--active');
        dot.setAttribute('aria-current', 'false');
      }
    });
  }
  
  startAutoplay() {
    const autoplayEnabled = this.container.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(this.container.dataset.autoplaySpeed) || 5;
    
    if (!autoplayEnabled || this.autoplayInterval) return;
    
    this.autoplayInterval = setInterval(() => {
      this.goToNext();
    }, autoplaySpeed * 1000);
  }
  
  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  getMaxIndex() {
    const visibleCards = Math.max(1, this.getVisibleCardsCount());
    return Math.max(0, this.totalCards - visibleCards);
  }

  setTrackPosition() {
    // Use getBoundingClientRect for more accurate measurements
    const cardRect = this.cards[0].getBoundingClientRect();
    const cardWidth = cardRect.width;
    
    // Parse gap value properly (handles "px", "rem", etc.)
    const gapString = getComputedStyle(this.track).gap;
    const gap = this.parseGapValue(gapString);
    
    const translateX = -(this.currentIndex * (cardWidth + gap));
    this.track.style.transform = `translateX(${translateX}px)`;
  }

  handleResize() {
    const maxIndex = this.getMaxIndex();
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
    }
    this.setTrackPosition();
    this.updateNavigation();
    this.updateDots();
  }
  
  destroy() {
    this.pauseAutoplay();
    
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.goToPrevious);
    }
    
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.goToNext);
    }
    
    this.dots.forEach((dot, index) => {
      dot.removeEventListener('click', () => this.goToSlide(index));
    });

    window.removeEventListener('resize', this.handleResize);
  }
}

// Initialize carousels when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('[data-section-type="hc-collections-carousel"]');
  
  carousels.forEach(carousel => {
    // Set autoplay data attributes
    const autoplaySetting = carousel.querySelector('[data-autoplay]');
    if (autoplaySetting) {
      carousel.dataset.autoplay = autoplaySetting.value;
    }
    
    const autoplaySpeedSetting = carousel.querySelector('[data-autoplay-speed]');
    if (autoplaySpeedSetting) {
      carousel.dataset.autoplaySpeed = autoplaySpeedSetting.value;
    }
    
    new CollectionsCarousel(carousel);
  });
});

// Handle Shopify section events
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.dataset.sectionType === 'hc-collections-carousel') {
    new CollectionsCarousel(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.dataset.sectionType === 'hc-collections-carousel') {
    const carousel = event.target.collectionsCarousel;
    if (carousel) {
      carousel.destroy();
    }
  }
});
